<template>
  <AIChatActions
    :class="{ 'tw:p-4': !card, 'tw:p-2': card }"
    :copy-text="copyText"
  >
    <AISummaryStreamFeedback
      v-if="stream"
      :ai-feature-configuration-id="aiFeatureConfigurationId"
      :ai-input="feedbackInput"
      :ai-result="feedbackResult"
      data-testid="feedback"
      :source="feedbackSource"
      :stream-id="streamId"
      @feedback-recorded="onFeedbackRecorded"
    />
  </AIChatActions>
</template>

<script setup lang="ts">
import { computed } from "vue";

import AIChatActions from "shared/modules/AI/components/AIChatActions/AIChatActions.vue";
import { type AIFeedbackRecordedEvent } from "shared/modules/AI/composables/useAIFeedback";
import { useAIStore } from "shared/modules/AI/stores/useAIStore";
import { type AISummary } from "shared/modules/AI/stores/useAIStore";
import AISummaryStreamFeedback from "shared/modules/AISummary/components/AISummaryStreamFeedback/AISummaryStreamFeedback.vue";
import useStreamsStore from "shared/stores/streams";
import { type Nullable, type Stream } from "shared/types";

interface AISummaryStreamResponseProps {
  card?: boolean;
  streamId: number;
}

const props = defineProps<AISummaryStreamResponseProps>();

const aiStore = useAIStore();
const { getAISummary, setAISummary } = aiStore;

const streamsStore = useStreamsStore();
const { getStreamById } = streamsStore;

const stream = computed<Nullable<Stream>>(
  () => getStreamById(props.streamId) || null
);

const feedbackSource = computed<string>(() => {
  if (stream.value) return `${stream.value.label} - Mention Stream`;

  return "";
});

const aiStreamSummary = computed<Required<AISummary>>(() =>
  getAISummary(props.streamId)
);

const feedbackInput = computed<Record<string, unknown>>(() => ({
  after: aiStreamSummary.value.dateRange.after,
  before: aiStreamSummary.value.dateRange.before,
  streamId: props.streamId,
}));

const feedbackResult = computed<Record<string, unknown>>(
  () => aiStreamSummary.value.result || {}
);

const aiFeatureConfigurationId = computed<number | undefined>(
  () => aiStreamSummary.value.result?.aiFeatureConfigurationId
);

const copyText = computed<string>(
  () =>
    aiStreamSummary.value.result?.topics
      .map((topic) => `${topic.title}\n${topic.summary}`)
      .join("\n\n") || ""
);

function onFeedbackRecorded(feedback: AIFeedbackRecordedEvent) {
  setAISummary(props.streamId, feedback);
}
</script>
